import {
  Edit as EditIcon,
  Copy as CopyIcon,
  DeleteCMS as DeleteIcon,
} from 'common/components/svgs';
/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { Popconfirm, Popover, Divider, message, Tooltip } from 'antd';
import { platformEntity, entityAction } from '~client/constants';
import { bookingJourneyType } from 'common/constants/booking';
import showElementByAuth from '~helpers/auth';
import handleTenancy from '~helpers/tenancy-preview';
import formatPrice from '~helpers/currency';
import { dayFormatType, formatDay } from '~helpers/day';
import { inventoryType as unitInventoryType } from '~constants/listing-management';
import { CopyText as CopyTextIcon } from 'common/components/svgs';
import { canDisplayExternalId } from '~helpers/canDisplayExternalId';
import { decodeBase64 } from 'common/helpers/decodeBase64';

const openEndDate = '9999-12-31T00:00:00+00:00';

export default class ViewModal extends React.PureComponent {
  constructor() {
    super();

    this.state = {
      copyListingPopoverVisible: false,
    };
  }

  formatListingData = () => {
    const { listingData, unitData } = this.props;
    const tenancyDetails = [];
    const availabilityAndPrice = [];
    const generalSetting = [];
    const isInstantBook = this.props?.property?.bookingJourney === bookingJourneyType.INSTANT_BOOK;

    if (listingData) {
      tenancyDetails.push({
        value: this.getTenancyLength().moveIn || '-',
        keyLabel: 'move_in',
      });
      tenancyDetails.push({
        value: this.getTenancyLength().moveOut || '-',
        keyLabel: 'move_out',
      });
      tenancyDetails.push({
        value: this.getTenancyLength().stay || '-',
        keyLabel: 'tenancy_length',
      });

      if (listingData.type === 'PLACEHOLDER') {
        availabilityAndPrice.push({
          value: this.props.t('cms.listing.modal.place_holder.label'),
          keyLabel: isInstantBook ? 'availability_number' : 'availability',
        });
      } else {
        availabilityAndPrice.push({
          value:
            isInstantBook || unitData?.inventoryType === unitInventoryType.ROOM_COUNT
              ? listingData.inventoryCount
              : listingData.availability
              ? this.props.t(
                  `cms:cms.listing.modal.availability.option.${listingData.availability.toLowerCase()}`,
                )
              : '-',
          keyLabel: isInstantBook ? 'availability_number' : 'availability',
        });
      }

      availabilityAndPrice.push({
        value: this.props.t(
          `cms.listing.modal.price_type.option.${
            listingData.priceMin &&
            listingData.priceMax &&
            listingData.priceMax !== listingData.priceMin
              ? 'range'
              : 'exact'
          }`,
        ),
        keyLabel: 'price_type',
      });
      availabilityAndPrice.push({
        value: listingData.priceMin
          ? formatPrice(listingData.priceMin, this.props.property.currency)
          : '-',
        keyLabel: 'price_min',
      });
      availabilityAndPrice.push({
        value: listingData.priceMax
          ? formatPrice(listingData.priceMax, this.props.property.currency)
          : '-',
        keyLabel: 'price_max',
      });
      availabilityAndPrice.push({
        value: listingData.discountType
          ? this.props.t(
              `cms.listing.modal.discount_type.options.${listingData.discountType.toLowerCase()}`,
            )
          : this.props.t('cms.listing.modal.discount_type.options.no_discount'),
        keyLabel: 'discount_type',
      });
      availabilityAndPrice.push({ value: listingData.discountValue, keyLabel: 'discount_value' });
      availabilityAndPrice.push({
        value: formatPrice(listingData.priceMin, this.props.property.currency),
        keyLabel: 'original_price',
      });
      availabilityAndPrice.push({ value: this.getCurrentPrice(), keyLabel: 'current_price' });
      if (isInstantBook) {
        availabilityAndPrice.push({
          value:
            listingData.turnaroundType === 'FIXED_DAYS'
              ? `${listingData.turnaroundTime} days`
              : listingData.turnaroundType
              ? this.props.t(
                  `cms.listing.modal.turnaround.value.${listingData.turnaroundType?.toLowerCase()}`,
                )
              : '-',
          keyLabel: 'turnaround',
        });
      }
      generalSetting.push({
        value: listingData.liveOn ? formatDay(dayFormatType.UKFORMAT, listingData.liveOn) : '-',
        keyLabel: 'live_on',
      });
      generalSetting.push({
        value: this.formatLiveUntil(listingData.liveUntil),
        keyLabel: 'live_until',
      });
      generalSetting.push({
        value: listingData.autoPriceAllowed ? 'Yes' : 'No',
        keyLabel: 'auto_price',
      });
      generalSetting.push({
        value: listingData.autoAvailAllowed ? 'Yes' : 'No',
        keyLabel: 'auto_avail',
      });
    }

    const newData = [
      { keyTitle: 'tenancy_details', data: tenancyDetails },
      { keyTitle: 'availability_and_price', data: availabilityAndPrice },
      { keyTitle: 'general_setting', data: generalSetting },
    ];

    return newData;
  };

  formatLiveUntil = liveUntil => {
    if (!liveUntil) {
      return '-';
    }

    if (liveUntil === openEndDate) {
      return this.props.t('cms.listing.modal.live_until.open_end');
    }

    return formatDay(dayFormatType.UKFORMAT, liveUntil);
  };

  getTenancyLength = () => {
    const { listingData, property } = this.props;
    let tenancyLengthValueMin;
    let tenancyLengthValueMax;
    if (listingData.tenancyLengthValue && isNaN(Number(listingData.tenancyLengthValue))) {
      const [min, max] = listingData.tenancyLengthValue.split('-');
      [tenancyLengthValueMin, tenancyLengthValueMax] = [Number(min), Number(max)];
    } else if (listingData.tenancyLengthValue) {
      tenancyLengthValueMin = Number(listingData.tenancyLengthValue);
    }

    const tenancy = handleTenancy(
      listingData.moveIn,
      listingData.moveOut,
      listingData.moveInType,
      listingData.moveOutType,
      property.billingCycle,
      listingData.tenancyLengthType,
      tenancyLengthValueMin,
      tenancyLengthValueMax,
    );

    return tenancy;
  };

  getCurrentPrice = () => {
    const { listingData, property } = this.props;
    let currentPrice = '-';
    if (
      listingData.priceMin &&
      listingData.discountValue &&
      listingData.discountType === 'ABSOLUTE'
    ) {
      currentPrice = formatPrice(
        listingData.priceMin - listingData.discountValue,
        property.currency,
      );
    } else if (
      listingData.priceMin &&
      listingData.discountValue &&
      listingData.discountType === 'PERCENTAGE'
    ) {
      // eslint-disable-next-line  no-mixed-operators
      currentPrice = formatPrice(
        listingData.priceMin * ((100 - listingData.discountValue) / 100),
        property.currency,
      );
    }

    return currentPrice;
  };

  extractListingId = originalStr => decodeBase64(originalStr).id;

  handleCopyListingPopoverVisible = visible => {
    this.setState({
      copyListingPopoverVisible: visible,
    });
  };

  handleCopyExternalId = externalId => {
    if (externalId) {
      navigator.clipboard.writeText(externalId).then(() => {
        message.success(this.props.t('cms.listing.modal.message.copied'));
      });
    }
  };

  formatUnconfirmedListingId = id => {
    let formatedId = id;
    const copyFromListingIndex = formatedId.indexOf('_copy-from-listing');

    if (copyFromListingIndex !== -1) {
      formatedId = formatedId.slice(0, copyFromListingIndex);
    }

    return formatedId.replace('unConfirmedFromCopy_', '');
  };

  render() {
    const { t, listingData, unitData, property } = this.props;
    const displayExternalId = canDisplayExternalId(
      property?.externalSystem,
      property?.feedControlled,
      listingData?.externalId,
    );

    return (
      <div
        className="listing-view-modal"
        ref={node => {
          this.tipsContainer = node;
        }}
      >
        <div className="listing-view-modal__listing-id-wrapper">
          {listingData?.id && (
            <div className="listing-view-modal__listing-id">
              {t('cms.listing.modal.lisitng_id.label', {
                id: listingData.unConfirmedFromCopy
                  ? this.extractListingId(this.formatUnconfirmedListingId(listingData.id))
                  : this.extractListingId(listingData.id),
              })}
            </div>
          )}
          {displayExternalId && (
            <>
              <Divider type="vertical" style={{ margin: '0 12px' }} />
              <div className="listing-view-modal__external-id">
                {t('cms.listing.modal.external_id.label', {
                  externalId: listingData.externalId,
                })}
              </div>
              <Tooltip
                className="listing-view-modal__copy-icon"
                title={t('cms.listing.modal.external_id.copy_tip')}
              >
                <CopyTextIcon
                  title="Copy"
                  onClick={() => {
                    this.handleCopyExternalId(listingData.externalId);
                  }}
                />
              </Tooltip>
            </>
          )}
        </div>
        <button
          type="button"
          className="listing-view-modal__close-btn"
          onClick={this.props.onClose}
        >
          <CloseOutlined style={{ fontSize: '12px' }} />
        </button>
        <div className="listing-view-modal__content">
          <div className="listing-view-modal__listing-detail">
            <For of={this.formatListingData()} each="item" index="index">
              <div key={item.keyTitle}>
                <div className="listing-view-modal__title">
                  <span className="listing-view-modal__title-text">
                    {t(`cms.listing.modal.${item.keyTitle}.title`)}
                  </span>
                </div>
                <For of={item.data} each="label" index="index">
                  <div
                    key={label.keyLabel}
                    className={classNames('listing-view-modal__label', {
                      'listing-view-modal__label--flex': label.keyLabel === 'label_bed_size_cm',
                    })}
                  >
                    {t(`cms.listing.modal.${label.keyLabel}.label`)}
                    <If
                      condition={[
                        'price_min',
                        'price_max',
                        'original_price',
                        'current_price',
                      ].includes(label.keyLabel)}
                    >
                      ({this.props.property.currency})
                    </If>
                    :
                    <span
                      className={classNames('listing-view-modal__label-value', {
                        'listing-view-modal__label-value--red':
                          label.keyLabel === 'availability' &&
                          label.value === t('cms.listing.modal.place_holder.label'),
                      })}
                    >
                      <If condition={label.keyLabel === 'availability'}>
                        {label.value ? label.value : 0}
                      </If>
                      <If condition={!(label.keyLabel === 'availability')}>
                        {label.value ? label.value : '-'}
                      </If>
                    </span>
                  </div>
                </For>
              </div>
            </For>
          </div>
        </div>
        <div className="listing-view-modal__footer">
          <div className="listing-view-modal__btns">
            <If
              condition={showElementByAuth(
                platformEntity.PROPERTIES_UNIT_TYPES,
                entityAction.DELETE,
              )}
            >
              <button
                className="listing-view-modal__btn"
                onClick={() => {
                  this.props.openModal('edit');
                }}
              >
                <EditIcon className="listing-view-modal__btn-icon" />
              </button>
              <span className="listing-view-modal__btns-line" />
            </If>
            <If
              condition={showElementByAuth(
                platformEntity.PROPERTIES_UNIT_TYPES,
                entityAction.CREATE,
              )}
            >
              <Choose>
                <When
                  condition={
                    listingData &&
                    (listingData.unConfirmedFromCopy || listingData.availability === null)
                  }
                >
                  <button className="listing-view-modal__btn" disabled>
                    <CopyIcon className="listing-view-modal__btn-icon" />
                  </button>
                </When>
                <Otherwise>
                  <Popover
                    content={
                      <div>
                        <div
                          className="listing-view-modal__copy-to-room"
                          role="presentation"
                          onClick={() => {
                            this.setState({ copyListingPopoverVisible: false });
                            this.props.openModal('copy');
                          }}
                        >
                          {t('cms.property.listing_management.copy_to_current_room.btn')}
                        </div>
                        <div
                          className="listing-view-modal__copy-to-room"
                          role="presentation"
                          onClick={() => {
                            this.setState({ copyListingPopoverVisible: false });
                            this.props.onOpenOtherRoomsModal(listingData);
                          }}
                        >
                          {t('cms.property.listing_management.copy_to_other_rooms.btn')}
                        </div>
                      </div>
                    }
                    trigger="click"
                    overlayClassName="listing-view-modal__copy-listing-wrap"
                    visible={this.state.copyListingPopoverVisible}
                    onVisibleChange={visible => this.handleCopyListingPopoverVisible(visible)}
                  >
                    <button
                      className="listing-view-modal__btn"
                      disabled={listingData && listingData.unConfirmedFromCopy}
                    >
                      <CopyIcon className="listing-view-modal__btn-icon" />
                    </button>
                  </Popover>
                </Otherwise>
              </Choose>
              <span className="listing-view-modal__btns-line" />
            </If>
            <If
              condition={showElementByAuth(
                platformEntity.PROPERTIES_UNIT_TYPES,
                entityAction.DELETE,
              )}
            >
              <Popconfirm
                title={t('cms.property.listing_management.delete_listing.tip')}
                placement="topRight"
                onConfirm={() => {
                  this.props.onDelete(listingData.id, unitData.id, listingData.unConfirmedFromCopy);
                  this.props.onClose();
                }}
                okType="danger"
                okText={t('cms.properties.edit.btn.yes')}
                cancelText={t('cms.properties.edit.btn.no')}
              >
                <button className="listing-view-modal__btn">
                  <DeleteIcon className="listing-view-modal__btn-icon" />
                </button>
              </Popconfirm>
            </If>
          </div>
        </div>
      </div>
    );
  }
}

ViewModal.propTypes = {
  t: PropTypes.func.isRequired,
  listingData: PropTypes.object,
  property: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  openModal: PropTypes.func,
  unitData: PropTypes.object,
  onOpenOtherRoomsModal: PropTypes.func.isRequired,
};

ViewModal.defaultProps = {
  t: () => {},
  onDelete: () => {},
  openModal: () => {},
  listingData: {},
  property: {},
  unitData: '',
  onOpenOtherRoomsModal: () => {},
};
